import { getFullUserUniversal, getSessionUniversal } from "@/app/access/session";
import { clientPromiseEffect } from "@crm/effect/client/promise-effect";
import { browserEffectRuntime } from "@crm/effect/client/runtime";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Effect } from "effect";
import { rpcCaller } from "@crm/rpc-client";
import { StudentInvitePublicRpc } from "@crm/rpc-contract/groups/students";
import type { ValidateStudentInviteTokenResult } from "@/app/identity/students/student-invite.effect";
import { inviteSearchSchema } from "../-lib/search-schema";

function runInviteRouteEffect<A, E>(effect: Effect.Effect<A, E>): Promise<A> {
  return browserEffectRuntime.runPromise(effect);
}

function loadInviteRouteEffect(token: string) {
  return Effect.gen(function* () {
    // 检查用户是否已登录
    const session = yield* promiseEffect(() => loadSession());
    if (session?.user) {
      // 已登录用户，先验证 token 获取学员信息
      const result = yield* promiseEffect(() =>
        studentInvitePublicRpc.validateInviteToken({ token }),
      );

      // token 无效或过期（result.success 恒为 true，直接按 data.valid 收窄分支）
      if (!result.data?.valid) {
        return yield* Effect.fail(
          redirect({
            to: "/",
            search: {
              error: result.data?.errorMessage || "邀请链接无效或已过期",
            },
          }),
        );
      }

      // 获取完整用户信息（包含 studentId）
      const fullUser = yield* promiseEffect(() => loadFullUser());
      const inviteStudentId = result.data.student?.id;
      const userStudentId = fullUser?.studentId;

      // 情况1：已关联同一学员 → 已完成注册
      if (userStudentId && userStudentId === inviteStudentId) {
        return yield* Effect.fail(
          redirect({
            to: "/",
            search: { error: "您已完成注册，无需重复操作" },
          }),
        );
      }

      // 情况2：已关联其他学员 → 提示已关联
      if (userStudentId && userStudentId !== inviteStudentId) {
        return yield* Effect.fail(
          redirect({
            to: "/",
            search: { error: "您的账号已关联其他学员，无法使用此邀请链接" },
          }),
        );
      }

      // 情况3：无学员关联 → 允许完成绑定（OTP 登录后重访 / OAuth 回调均适用）
      return {
        ...result.data,
        pendingRegistration: {
          userId: session.user.id,
        },
      } as InviteLoaderData;
    }

    // 未登录用户，正常流程
    const result = yield* promiseEffect(() =>
      studentInvitePublicRpc.validateInviteToken({ token }),
    );

    if (!result.success) {
      return yield* Effect.fail(
        redirect({
          to: "/login",
          search: {
            error: "服务器错误，请稍后重试",
          },
        }),
      );
    }

    if (!result.data?.valid) {
      return yield* Effect.fail(
        redirect({
          to: "/login",
          search: {
            error: result.data?.errorMessage || "邀请链接无效或已过期",
          },
        }),
      );
    }

    return result.data as InviteLoaderData;
  });
}

const studentInvitePublicRpc = rpcCaller(StudentInvitePublicRpc);

const loadSession = () =>
  typeof window === "undefined"
    ? import("@tanstack/react-start/server").then(({ getRequest }) =>
        getSessionUniversal(getRequest().headers),
      )
    : getSessionUniversal();

const loadFullUser = () =>
  typeof window === "undefined"
    ? import("@tanstack/react-start/server").then(({ getRequest }) =>
        getFullUserUniversal(getRequest().headers),
      )
    : getFullUserUniversal();

type InviteLoaderData = InviteTokenData & {
  pendingRegistration?: {
    userId: string;
  };
};

function promiseEffect<A>(run: () => PromiseLike<A>) {
  return clientPromiseEffect("邀请注册失败", run);
}

type InviteTokenData = ValidateStudentInviteTokenResult["data"];

export const Route = createFileRoute("/(auth)/invite/$token/")({
  head: () => ({
    meta: [{ title: "邀请注册 · imiker CRM" }],
  }),
  validateSearch: inviteSearchSchema,
  loader: ({ params }) => runInviteRouteEffect(loadInviteRouteEffect(params.token))
});
